import Link, { LinkProps } from 'next/link';
import React, { AnchorHTMLAttributes, forwardRef } from 'react';

interface CustomLinkProps extends LinkProps, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> {
	children?: React.ReactNode;
	/**
	 * Render a plain `<a>` instead of Next's `<Link>`, so the click is a full document navigation and never
	 * a client-side transition. Needed when the server has to see the click: the Everflow landing
	 * middleware only records a click (and cleans the URL) on document requests, and Next treats an
	 * absolute URL on our own origin as internal, so a `<Link>` would route it client-side and skip the
	 * middleware. The listing-ad cards set it. Only applies to string hrefs; other hrefs fall back to `<Link>`.
	 */
	native?: boolean;
}

/** Props `next/link` understands but a plain `<a>` does not. */
const LINK_ONLY_PROPS = ['as', 'replace', 'scroll', 'shallow', 'passHref', 'locale', 'legacyBehavior', 'onNavigate'] as const;

/** Everything in `props` that is a valid anchor attribute — the `<Link>`-only props removed. */
const toAnchorProps = (props: Omit<CustomLinkProps, 'href' | 'native'>): AnchorHTMLAttributes<HTMLAnchorElement> => {
	const anchorProps: Partial<Omit<CustomLinkProps, 'href' | 'native'>> = { ...props };
	for (const key of LINK_ONLY_PROPS) delete anchorProps[key];
	return anchorProps as AnchorHTMLAttributes<HTMLAnchorElement>;
};

const CustomLink = forwardRef<HTMLAnchorElement, CustomLinkProps>(({ prefetch = false, native = false, ...props }, ref) => {
	if (native && typeof props.href === 'string') {
		const { href, ...rest } = props;
		return (
			<a {...toAnchorProps(rest)} href={href} ref={ref}>
				{props.children}
			</a>
		);
	}

	return (
		<Link {...props} prefetch={prefetch} ref={ref}>
			{props.children}
		</Link>
	);
});

CustomLink.displayName = 'CustomLink';

export default CustomLink;
